/**
 * Control Presupuestario → Company Metrics: réplica del reporte "CM 2.0" de
 * Finance (07/10/2026). Una tabla de métricas por fila con meses / trimestres /
 * FY y año; secciones Company Metrics (4 sub-pestañas), Practices, Segment y P&L
 * (bloques con hijos plegables). Lee `financePnl.getCompanyMetrics`: la planilla
 * como base + BigQuery en vivo donde hay fuente; cada fila dice de dónde sale.
 */

import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { buildPlTree, childLabel, CM_EMPH, CM_PL_BLOCKS, CM_SECTIONS } from '../../../../../shared/company-metrics';
import type { CmRow, CmSectionKey } from '../../../../../shared/types/company-metrics';
import { usePnlFmt, type PnlFmt } from './pnl-format';
import { Seg, SegLabel } from './pnl-ui';

type Period = 'months' | 'quarters' | 'fy';

function valuesOf(row: CmRow, year: string, period: Period): Array<number | null> {
  const d = row.data[year];
  const n = period === 'months' ? 12 : period === 'quarters' ? 4 : 1;
  if (!d) return Array.from({ length: n }, () => null);
  return period === 'months' ? d.months : period === 'quarters' ? d.quarters : [d.fy];
}

function cell(fmt: PnlFmt, v: number | null, pct: boolean): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return pct ? fmt.pct(v * 100, 0) : fmt.num(v, 1);
}

export function CompanyMetricsDashboard() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const query = trpc.financePnl.getCompanyMetrics.useQuery(undefined, { staleTime: 5 * 60 * 1000, retry: false });
  const [section, setSection] = useState<CmSectionKey>('company');
  const [sub, setSub] = useState('financial');
  const [year, setYear] = useState('2026');
  const [period, setPeriod] = useState<Period>('months');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState(false);
  const fmt = usePnlFmt(Number(year));

  const refresh = async () => {
    setRefreshing(true);
    try {
      const fresh = await utils.financePnl.getCompanyMetrics.fetch({ refresh: true });
      utils.financePnl.getCompanyMetrics.setData(undefined, fresh);
      toast.success(t('budget_control.pnl.refreshed'));
    } catch (e) {
      toast.error(t('budget_control.pnl.refresh_failed', { error: e instanceof Error ? e.message : String(e) }));
    } finally {
      setRefreshing(false);
    }
  };

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <WinclapLoader size={40} />
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t('budget_control.company.load_error_title')}</AlertTitle>
        <AlertDescription>
          {t('budget_control.company.load_error_desc')} {query.error?.message}
        </AlertDescription>
      </Alert>
    );
  }
  const data = query.data;
  const sectionDef = CM_SECTIONS.find((s) => s.key === section) ?? CM_SECTIONS[0];
  const pickSection = (k: CmSectionKey) => {
    setSection(k);
    setSub((CM_SECTIONS.find((s) => s.key === k) ?? CM_SECTIONS[0]).subs[0].key);
  };
  const rows: CmRow[] = section === 'pl' ? data.sections.pl.main ?? [] : data.sections[section]?.[sub] ?? [];
  const labels =
    period === 'months'
      ? Array.from({ length: 12 }, (_, i) => fmt.monthShort(i + 1))
      : period === 'quarters'
        ? ['Q1', 'Q2', 'Q3', 'Q4']
        : [`FY${year}`];
  const hasSheet = rows.some((r) => r.data[year] && r.sources[year] === 'sheet');
  const hasLive = rows.some((r) => r.sources[year] === 'live');

  const toggle = (m: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m);
      else next.add(m);
      return next;
    });

  const byMetric = new Map(rows.map((r) => [r.metric, r]));
  const tree = section === 'pl' ? buildPlTree(rows) : { childrenOf: {} as Record<string, string[]>, parentOf: {} as Record<string, string> };

  const td = 'whitespace-nowrap border-b border-border px-2.5 py-1.5 text-right font-mono text-[12.5px] tabular-nums';
  const first = 'sticky left-0 z-1 w-[150px] min-w-[150px] max-w-[150px] sm:w-[230px] sm:min-w-[230px] sm:max-w-[230px] border-b border-border bg-card px-3 py-1.5 text-left text-[12.5px]';

  const renderRow = (row: CmRow, depth: number): React.ReactNode => {
    const pct = row.format === 'pct';
    const kids = tree.childrenOf[row.metric] ?? [];
    const open = expanded.has(row.metric);
    const sheet = row.data[year] && row.sources[year] === 'sheet';
    return (
      <Fragment key={row.metric}>
        <tr className={cn('hover:bg-muted/50', CM_EMPH.has(row.metric) && 'font-semibold')}>
          <td className={cn(first, depth > 0 && 'text-muted-foreground', CM_EMPH.has(row.metric) && 'text-primary')} style={{ paddingLeft: 12 + depth * 18 }}>
            <span className="flex items-center gap-1">
              {kids.length > 0 ? (
                <button type="button" onClick={() => toggle(row.metric)} aria-expanded={open} className="flex items-center gap-1 text-left">
                  <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 transition-transform', open && 'rotate-90')} />
                  {depth > 0 ? childLabel(row.metric) : row.metric}
                </button>
              ) : (
                <span>{depth > 0 ? childLabel(row.metric) : row.metric}</span>
              )}
              {sheet && (
                <span className="ml-1 shrink-0 rounded bg-amber-100 px-1 text-[9.5px] font-semibold uppercase text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" title={t('budget_control.company.sheet_hint')}>
                  {t('budget_control.company.sheet_badge')}
                </span>
              )}
            </span>
          </td>
          {valuesOf(row, year, period).map((v, i) => (
            <td key={i} className={cn(td, pct && 'text-muted-foreground', v !== null && v < 0 && 'text-(--pnl-unfav)')}>
              {cell(fmt, v, pct)}
            </td>
          ))}
        </tr>
        {open && kids.map((k) => byMetric.get(k) && renderRow(byMetric.get(k)!, depth + 1))}
      </Fragment>
    );
  };

  const body: React.ReactNode[] = [];
  if (section === 'pl') {
    const blocks = CM_PL_BLOCKS[sub === 'ratios' ? 'ratios' : 'main'];
    for (const b of blocks) {
      const blockRows = b.metrics.map((m) => byMetric.get(m)).filter((r): r is CmRow => !!r);
      if (!blockRows.length) continue;
      body.push(
        <tr key={`h-${b.title}`}>
          <td colSpan={labels.length + 1} className="sticky left-0 border-b border-border bg-muted px-3 pb-1.5 pt-3 text-[11px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
            {b.title}
          </td>
        </tr>
      );
      for (const r of blockRows) body.push(renderRow(r, 0));
    }
  } else {
    for (const r of rows) body.push(renderRow(r, 0));
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Seg
          value={section}
          onChange={pickSection}
          ariaLabel={t('budget_control.company.section_aria')}
          options={[
            { value: 'company', label: t('budget_control.company.section_company') },
            { value: 'practices', label: t('budget_control.company.section_practices') },
            { value: 'segment', label: t('budget_control.company.section_segment') },
            { value: 'pl', label: t('budget_control.company.section_pl') },
          ]}
        />
        <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <span>{t('budget_control.creators.updated', { date: fmt.dateTime(data.generatedAt) })}</span>
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}>
            <RefreshCw className={refreshing ? 'mr-1.5 h-3.5 w-3.5 animate-spin' : 'mr-1.5 h-3.5 w-3.5'} />
            {t('budget_control.pnl.refresh')}
          </Button>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-border">
        {sectionDef.subs.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setSub(s.key)}
            aria-pressed={sub === s.key}
            className={cn(
              '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-xs font-semibold transition-colors',
              sub === s.key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <SegLabel>{t('budget_control.company.year')}</SegLabel>
          <Seg value={year} onChange={setYear} ariaLabel={t('budget_control.company.year')} options={data.years.map((y) => ({ value: y, label: y }))} />
        </div>
        <div className="flex items-center gap-2">
          <SegLabel>{t('budget_control.company.period')}</SegLabel>
          <Seg
            value={period}
            onChange={setPeriod}
            ariaLabel={t('budget_control.company.period')}
            options={[
              { value: 'months', label: t('budget_control.company.period_months') },
              { value: 'quarters', label: t('budget_control.company.period_quarters') },
              { value: 'fy', label: 'FY' },
            ]}
          />
        </div>
        <span className="ml-auto text-[11px] text-muted-foreground">{t('budget_control.company.units')}</span>
      </div>

      {data.warnings.length > 0 && (
        <Alert>
          <AlertTitle>{t('budget_control.company.warnings_title')}</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4 text-xs">
              {data.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <Card className="overflow-hidden p-0 shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr>
                <th className={cn(first, 'z-2 bg-muted text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground')}>{t('budget_control.company.metric')}</th>
                {labels.map((l) => (
                  <th key={l} className="whitespace-nowrap border-b border-border bg-muted px-2.5 py-2 text-right text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {l}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.length ? (
                body
              ) : (
                <tr>
                  <td colSpan={labels.length + 1} className="py-10 text-center text-sm text-muted-foreground">
                    {t('budget_control.company.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-xs text-muted-foreground">
        {hasLive && t('budget_control.company.legend_live')} {hasSheet && t('budget_control.company.legend_sheet')} {t('budget_control.company.source_note')}
      </p>
    </div>
  );
}
